import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { 
  LayoutDashboard, Users, MessageSquare, PhoneCall, PlusCircle, 
  Eye, LogOut, ChevronDown, ChevronRight, UserPlus, UserCheck, X 
} from "lucide-react";
import { useAdminAuth } from "../context/AdminAuthContext";

// Props interface
interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const AdminSidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { role, logout } = useAdminAuth();
  
  const canApprove = role === "SUPER_ADMIN" || role === "ADMIN";

  // Dropdown States
  const [openQueries, setOpenQueries] = useState(false);
  const [openQuotations, setOpenQuotations] = useState(false);
  const [openTeam, setOpenTeam] = useState(false);

  // Link Style
  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 px-4 py-3 text-sm font-medium transition-all rounded-lg ${
      isActive
        ? "bg-amber-600 text-white shadow-lg shadow-amber-900/20"
        : "text-zinc-400 hover:text-white hover:bg-zinc-800"
    }`;

  return (
    <>
      {/* SIDEBAR CONTAINER */}
      <div 
        className={`
          fixed top-0 left-0 z-50 h-screen w-64 bg-[#0c0c0c] border-r border-zinc-800 flex flex-col font-[Serif] shadow-2xl
          transform transition-transform duration-300 ease-in-out
          ${isOpen ? "translate-x-0" : "-translate-x-full"} 
          md:translate-x-0
        `}
      >
        
        {/* LOGO AREA */}
        <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
          <h1 className="text-xl font-bold text-white tracking-widest">
            LUXURIYA <span className="text-amber-500">ADMIN</span>
          </h1>
          
          {/* Close Button (Visible ONLY on Mobile) */}
          <button onClick={onClose} className="md:hidden text-zinc-500 hover:text-white">
            <X size={20} />
          </button>
        </div>

        {/* SCROLLABLE MENU */}
        <div className="flex-1 overflow-y-auto py-6 px-3 space-y-2 scrollbar-hide">
          
          {/* Dashboard */}
          <NavLink 
            to="/homes-back/dashboard" 
            className={linkClass}
            onClick={() => onClose()} // Mobile pe click karne par sidebar band ho
          >
            <LayoutDashboard size={18} /> Dashboard
          </NavLink>

          {/* User Queries */}
          <div className="mt-4">
            <button 
              onClick={() => setOpenQueries(!openQueries)}
              className="w-full flex items-center justify-between px-4 py-2 text-xs uppercase tracking-wider text-zinc-500 font-bold hover:text-amber-500 transition-colors"
            >
              User Queries
              {openQueries ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
            </button>
            {openQueries && (
              <div className="ml-4 mt-2 space-y-1 border-l border-zinc-800 pl-2">
                <NavLink to="/homes-back/consulting-queries" className={linkClass} onClick={onClose}>
                  <MessageSquare size={16} /> Consulting
                </NavLink>
                <NavLink to="/homes-back/contact-enquiries" className={linkClass} onClick={onClose}>
                  <PhoneCall size={16} /> Contact
                </NavLink>
              </div>
            )}
          </div>

          {/* Quotations */}

          {/* Team Management */}
          <div className="mt-4">
            <button 
              onClick={() => setOpenTeam(!openTeam)}
              className="w-full flex items-center justify-between px-4 py-2 text-xs uppercase tracking-wider text-zinc-500 font-bold hover:text-amber-500 transition-colors"
            >
              Team Management
              {openTeam ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
            </button>
            {openTeam && (
              <div className="ml-4 mt-2 space-y-1 border-l border-zinc-800 pl-2">
                {canApprove && (
                  <NavLink to="/homes-back/team/approvals" className={linkClass} onClick={onClose}>
                    <UserCheck size={16} /> Approvals
                  </NavLink>
                )}
                <NavLink to="/homes-back/team/subordinates" className={linkClass} onClick={onClose}>
                  <Users size={16} /> My Subordinates
                </NavLink>
              </div>
            )}
          </div>

        </div>

        {/* LOGOUT */}
        <div className="p-4 border-t border-zinc-800">
          <button 
            onClick={logout}
            className="w-full flex items-center gap-3 px-4 py-3 text-red-400 hover:bg-red-900/10 hover:text-red-300 rounded-lg transition-all text-sm font-bold uppercase tracking-wide"
          >
            <LogOut size={18} /> Logout
          </button>
        </div>

      </div>
    </>
  );
};

export default AdminSidebar;